import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  DragEvent,
} from 'react';
import { Search, LucideIcon } from 'lucide-react';
import { NodeStruct, NodeCategory } from '@/types/node-types';
import { NodeItem } from './node-list';
import { GroupedToolNodeList } from './grouped-tool-node-list';
import { useNodeSelection } from '@/hooks/use-node-selection';
import { useDnD } from '@/context/dnd-context';
import { cn } from '@/lib/utils';
import { SelectionFooter } from './selection-footer';
import { setMultiNodeDragImage } from '@/stores/helpers/node-drop-helpers';
import { DEFAULT_EXPANDED_MCP_SERVERS } from '@/constants/node-catalog';

interface CustomAction {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  primaryColor: string;
}

interface NodeSelectorProps {
  nodes: NodeStruct[];
  primaryColor: string;
  onNodeClick?: (node: NodeStruct) => void;
  className?: string;
  placeholder?: string;
  maxHeight?: string;
  listTitle?: string;
  customAction?: CustomAction;
  isLoading?: boolean;
}

export const NodeSelector: React.FC<NodeSelectorProps> = ({
  nodes,
  primaryColor: _primaryColor,
  onNodeClick,
  className = '',
  placeholder = 'Search tools',
  maxHeight = 'max-h-60',
  listTitle,
  customAction,
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const customActionRef = useRef<HTMLDivElement>(null);
  const [, setDndPayload] = useDnD();
  /** When showing grouped tools, this holds the flat display order (for Enter selection) */
  const flatDisplayOrderRef = useRef<NodeStruct[] | null>(null);

  const {
    isSelected,
    getCount,
    selectSingle,
    selectMultiple,
    addToSelection,
    toggleSelect,
    selectRange,
    clearSelection,
    selectedNodeIds,
    selectionCounts,
    totalSelectedCount,
  } = useNodeSelection();

  // Filter nodes based on search query. Matches the tool display name OR the
  // group/server name (tool_category) so e.g. "eBay Seller" surfaces that
  // server's tools even when no individual tool name contains the query.
  // Name matches rank above category-only matches.
  const filteredNodes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return nodes;

    const nameMatches: NodeStruct[] = [];
    const categoryMatches: NodeStruct[] = [];
    nodes.forEach((node) => {
      if ((node.display_name || '').toLowerCase().includes(query)) {
        nameMatches.push(node);
      } else if (node.tool_category?.toLowerCase().includes(query)) {
        categoryMatches.push(node);
      }
    });
    return [...nameMatches, ...categoryMatches];
  }, [nodes, searchQuery]);

  const hasCustomAction = !!customAction;
  const totalItems = filteredNodes.length + (hasCustomAction ? 1 : 0);

  // Check if all filtered nodes are Tool category (used for grouped display)
  const allToolNodes = useMemo(() => {
    return (
      filteredNodes.length > 0 &&
      filteredNodes.every((node) => node.node_type === NodeCategory.TOOL)
    );
  }, [filteredNodes]);

  // Stable key for filtered list identity; resets highlight when list content/order changes
  const filteredListKey = useMemo(
    () => filteredNodes.map((n) => n.node_id).join(','),
    [filteredNodes],
  );

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredListKey, searchQuery]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listContainerRef.current) {
      if (hasCustomAction && highlightedIndex === 0) {
        return;
      }
      const nodeIndex = hasCustomAction
        ? highlightedIndex - 1
        : highlightedIndex;
      const highlightedElement = listContainerRef.current.querySelector(
        `[data-node-index="${nodeIndex}"]`,
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, hasCustomAction]);

  // --- Selection handlers (same pattern as sidebar) ---

  const handleToggleSelect = useCallback(
    (node: NodeStruct) => {
      toggleSelect(node.node_id);
    },
    [toggleSelect],
  );

  const handleSelectSingle = useCallback(
    (node: NodeStruct) => {
      selectSingle(node.node_id);
    },
    [selectSingle],
  );

  const handleShiftSelect = useCallback(
    (node: NodeStruct) => {
      const list =
        allToolNodes && flatDisplayOrderRef.current?.length
          ? flatDisplayOrderRef.current
          : filteredNodes;
      const orderedNodeIds = list.map((n) => n.node_id);
      selectRange(node.node_id, orderedNodeIds);
    },
    [selectRange, filteredNodes, allToolNodes],
  );

  const handleSelectCategory = useCallback(
    (categoryNodes: NodeStruct[]) => {
      selectMultiple(categoryNodes.map((n) => n.node_id));
    },
    [selectMultiple],
  );

  const handleAddToSelection = useCallback(
    (categoryNodes: NodeStruct[]) => {
      addToSelection(categoryNodes.map((n) => n.node_id));
    },
    [addToSelection],
  );

  const handleRangeSelect = useCallback(
    (targetNodeId: string, allOrderedNodeIds: string[]) => {
      selectRange(targetNodeId, allOrderedNodeIds);
    },
    [selectRange],
  );

  // Each node is added individually because slot-card's handleNodeSelect
  // performs per-node validation, position stacking, and connection creation
  // that depends on the cumulative canvas state after each addition.
  // Uses the full `nodes` prop (not `filteredNodes`) so selections that
  // span across search-filter changes are never silently dropped.
  const handleAddAllSelected = useCallback(() => {
    if (!onNodeClick || selectionCounts.size === 0) return;
    const selectedStructs = nodes
      .filter((n) => selectedNodeIds.has(n.node_id))
      .flatMap((n) => {
        const count = selectionCounts.get(n.node_id) ?? 1;
        return Array.from({ length: count }, () => n);
      });
    selectedStructs.forEach((struct) => onNodeClick(struct));
    clearSelection();
  }, [onNodeClick, nodes, selectedNodeIds, selectionCounts, clearSelection]);

  // Double-click on a node row immediately adds it without going through the
  // select → "Add all" two-step. Clears any pre-existing selection first so we
  // don't surprise the user with stale selections sticking around afterwards.
  const handleDoubleClickAdd = useCallback(
    (node: NodeStruct) => {
      if (!onNodeClick) return;
      clearSelection();
      onNodeClick(node);
    },
    [onNodeClick, clearSelection],
  );

  // Double-click on an MCP server row adds all sub-tools immediately, mirroring
  // what "Add all" would do after a single-click selection of the whole group.
  const handleDoubleClickAddCategory = useCallback(
    (categoryNodes: NodeStruct[]) => {
      if (!onNodeClick || categoryNodes.length === 0) return;
      clearSelection();
      categoryNodes.forEach((n) => onNodeClick(n));
    },
    [onNodeClick, clearSelection],
  );

  // --- Drag handlers (same pattern as sidebar) ---

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, node: NodeStruct) => {
      if (selectedNodeIds.has(node.node_id) && selectedNodeIds.size > 1) {
        const selectedIds = filteredNodes
          .filter((n) => selectedNodeIds.has(n.node_id))
          .flatMap((n) => {
            const count = selectionCounts.get(n.node_id) ?? 1;
            return Array(count).fill(n.node_id) as string[];
          });

        setDndPayload({ type: 'multiSelect', nodeIds: selectedIds });
        setMultiNodeDragImage(event, selectedIds.length);
      } else {
        const count = selectionCounts.get(node.node_id) ?? 1;
        if (count > 1) {
          setDndPayload({
            type: 'multiSelect',
            nodeIds: Array(count).fill(node.node_id) as string[],
          });
          setMultiNodeDragImage(event, count);
        } else {
          setDndPayload({ type: 'node', nodeId: node.node_id });
        }
      }
      event.dataTransfer.effectAllowed = 'move';
    },
    [setDndPayload, selectedNodeIds, selectionCounts, filteredNodes],
  );

  const handleDragStartCategory = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      categoryName: string,
      categoryNodes: NodeStruct[],
    ) => {
      setDndPayload({
        type: 'toolCategory',
        categoryName,
        nodeIds: categoryNodes.map((n) => n.node_id),
      });
      setMultiNodeDragImage(event, categoryNodes.length);
      event.dataTransfer.effectAllowed = 'move';
    },
    [setDndPayload],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) {
            if (hasCustomAction && highlightedIndex === 0) {
              customAction?.onClick();
            } else {
              const nodeIndex = hasCustomAction
                ? highlightedIndex - 1
                : highlightedIndex;
              // Use display order for tool nodes (grouped list); otherwise filteredNodes order
              const list =
                allToolNodes && flatDisplayOrderRef.current?.length
                  ? flatDisplayOrderRef.current
                  : filteredNodes;
              if (nodeIndex >= 0 && nodeIndex < list.length) {
                onNodeClick?.(list[nodeIndex]);
              }
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setHighlightedIndex(-1);
          clearSelection();
          break;
      }
    },
    [
      filteredNodes,
      highlightedIndex,
      onNodeClick,
      totalItems,
      hasCustomAction,
      customAction,
      allToolNodes,
      clearSelection,
    ],
  );

  // Stop event propagation to prevent node focus/selection when interacting with this component
  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={`min-w-[320px] space-y-1 pb-2 ${className}`}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onFocus={stopPropagation}
    >
      {/* Search Input */}
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
        <input
          type='text'
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
          onFocus={stopPropagation}
          className='flex h-9 w-full! rounded-none! border-0! border-b! border-gray-300! bg-transparent! pr-3! py-1! pl-10! text-sm! placeholder:text-gray-500! focus:outline-none! focus:ring-0! focus:border-gray-300!'
          autoFocus
        />
      </div>

      {/* Custom Action - shown below search */}
      {customAction && (
        <div ref={customActionRef} className='px-2 pt-1'>
          <button
            type='button'
            className={`flex items-center gap-3 w-full p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-900 transition-colors text-left ${
              highlightedIndex === 0 ? 'ring-2 ring-primary' : ''
            }`}
            onClick={(e) => {
              stopPropagation(e);
              customAction.onClick();
            }}
            onMouseDown={stopPropagation}
          >
            <div
              className='flex items-center justify-center rounded-lg shrink-0'
              style={{
                backgroundColor: customAction.primaryColor,
                width: '32px',
                height: '32px',
              }}
            >
              <customAction.icon className='w-5 h-5 text-white' />
            </div>
            <span className='text-sm font-medium text-gray-900 dark:text-white'>
              {customAction.label}
            </span>
          </button>
        </div>
      )}

      {listTitle && (
        <div className='px-2 py-1'>
          <p className='text-sm font-medium text-muted-foreground'>
            {listTitle}
          </p>
        </div>
      )}

      {/* Node List - nowheel class allows scrolling within ReactFlow nodes */}
      <div
        ref={listContainerRef}
        className={`nowheel space-y-1 ${maxHeight} overflow-y-auto px-2 py-1 select-none`}
        onWheel={stopPropagation}
        onMouseDown={(e) => {
          if (e.shiftKey) {
            e.preventDefault();
          }
        }}
      >
        {allToolNodes ? (
          <GroupedToolNodeList
            nodes={filteredNodes}
            onDragStart={handleDragStart}
            onDragStartCategory={handleDragStartCategory}
            onAddNode={onNodeClick}
            showGripIcon={true}
            initialSubCategoryState={DEFAULT_EXPANDED_MCP_SERVERS}
            isNodeSelected={isSelected}
            getNodeSelectionCount={getCount}
            onToggleSelect={handleToggleSelect}
            onSelectSingle={handleSelectSingle}
            onShiftSelect={handleShiftSelect}
            onDoubleClickAdd={handleDoubleClickAdd}
            onDoubleClickAddCategory={handleDoubleClickAddCategory}
            onSelectCategory={handleSelectCategory}
            onAddToSelection={handleAddToSelection}
            onRangeSelect={handleRangeSelect}
            onDragEnd={clearSelection}
            highlightedIndex={
              hasCustomAction ? highlightedIndex - 1 : highlightedIndex
            }
            flatDisplayOrderRef={flatDisplayOrderRef}
          />
        ) : (
          <>
            {filteredNodes.map((node, idx) => {
              const adjustedHighlightedIndex = hasCustomAction
                ? highlightedIndex - 1
                : highlightedIndex;

              return (
                <div
                  key={node.node_id || idx}
                  data-node-index={idx}
                  className={cn(
                    adjustedHighlightedIndex === idx &&
                      'ring-2 ring-primary rounded-lg',
                  )}
                >
                  <NodeItem
                    node={node}
                    onDragStart={handleDragStart}
                    onDragEnd={clearSelection}
                    onAddNode={onNodeClick}
                    showGripIcon={true}
                    selected={isSelected(node.node_id)}
                    selectionCount={getCount(node.node_id)}
                    onToggleSelect={handleToggleSelect}
                    onSelectSingle={handleSelectSingle}
                    onShiftSelect={handleShiftSelect}
                    onDoubleClickAdd={handleDoubleClickAdd}
                  />
                </div>
              );
            })}
          </>
        )}

        {filteredNodes.length === 0 && searchQuery.trim() && (
          <div className='text-center text-sm text-muted-foreground py-4'>
            No nodes found matching &quot;{searchQuery}&quot;
          </div>
        )}

        {filteredNodes.length === 0 && !searchQuery.trim() && isLoading && (
          <div className='text-center text-sm text-muted-foreground py-4'>
            Loading nodes...
          </div>
        )}

        {filteredNodes.length === 0 &&
          !searchQuery.trim() &&
          !isLoading &&
          nodes.length === 0 && (
            <div className='text-center text-sm text-muted-foreground py-4'>
              No compatible nodes available
            </div>
          )}
      </div>
      <SelectionFooter
        totalCount={totalSelectedCount}
        onAddAll={handleAddAllSelected}
      />
    </div>
  );
};
